import React from 'react'
import { useTranslation } from 'react-i18next'
import { Log } from '../../types/log'
import { PagedResponse } from '../../types/pagedResponse'
import { get } from '../../utils/service'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import { getFullscreenElement } from '../../utils/graphUtils'
import LoadingButton from '@mui/lab/LoadingButton'
import { getLogName, getLogsUrl } from '../../utils/logUtils'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { toReadableTimestamp } from '../../utils/dateUtils'
import { Alert, Button, CircularProgress, Pagination } from '@mui/material'
import SearchField from './logSearchField'
import { useContextHook } from '../../store/context'
import { useNavigate, useParams } from 'react-router-dom'
interface compareLogDialogProps {
  onClose: () => void
  log: Log
}

const CompareLogDialog = (props: compareLogDialogProps): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const { state } = useContextHook()
  const [compareLogs, setCompareLogs] = React.useState<Array<Log> | null>(null)
  const [isLoadingLogs, setIsLoadingLogs] = React.useState<boolean>(false)
  const [hasLoadError, setHasLoadError] = React.useState<boolean>(false)
  const [selectedCompares, setSelectedCompares] = React.useState<Array<Log> | null>(props.log ? [props.log] : null)
  const [logSearchString, setLogSearchString] = React.useState<string>('')
  const [logsTotalPages, setLogsTotalPages] = React.useState(0)
  const [logsActivePage, setLogsActivePage] = React.useState(0)
  const logsPerPage = window.innerHeight > 900 ? 10 : 5

  React.useEffect(() => {
    const loadLogsUrl = getLogsUrl(state.currentMachine?.id, logSearchString)
    setIsLoadingLogs(true)
    setHasLoadError(false)

    get<PagedResponse<Log>>(loadLogsUrl)
      .then((response) => {
        let compareableLogs = response.data.results.filter((compare) => compare.id !== props.log.id)
        setCompareLogs(compareableLogs)
        const totalPages = Math.ceil(compareableLogs?.length / logsPerPage)
        setLogsTotalPages(totalPages)
        setIsLoadingLogs(false)
      })
      .catch(() => {
        setIsLoadingLogs(false)
        setHasLoadError(true)
      })
  }, [logSearchString, props.log.id, state.currentMachine?.id, logsPerPage])

  const handleSearch = (e: string) => {
    setLogsActivePage(0)
    setLogSearchString(e)
  }

  const handleSelectedCompare = (x: Log) => {
    if (selectedCompares?.find((compare) => compare.id === x.id)) {
      setSelectedCompares((selectedCompares || []).filter((compare) => compare.id !== x.id))
    } else {
      setSelectedCompares((selectedCompares) => [...(selectedCompares || []), x])
    }
  }

  const goToLogsView = () => {
    //Navigates back to Logs view and set the selected compares to compareLogData
    //UseEffect sets the viewmode to 'compare'
    navigate(`/machine/${params.machineId}/logs/`, { state: { compareLogData: selectedCompares } })
  }
  const calculatePageRange = () => {
    const startIndex = logsActivePage * logsPerPage
    const endIndex = startIndex + logsPerPage
    return { startIndex, endIndex }
  }

  return (
    <Dialog open={true} onClose={props.onClose} fullWidth sx={{ height: '100%' }} container={getFullscreenElement()}>
      <DialogContent>
        <Stack spacing={1}>
          <Typography>{t('logview.comparelogwith')}</Typography>
          <SearchField placeholder={t('logview.searchlogs')} onSearch={handleSearch} />
          {isLoadingLogs && <CircularProgress />}
          {hasLoadError && <Alert severity="error">{t('logview.logsloaderror')}</Alert>}

          {compareLogs &&
            compareLogs.map((x, index) => {
              const { startIndex, endIndex } = calculatePageRange()
              if (index >= startIndex && index < endIndex) {
                return (
                  <LoadingButton
                    key={x.url}
                    sx={{ textTransform: 'none', textAlign: 'left', justifyContent: 'flex-start' }}
                    variant={selectedCompares?.find((compare) => compare.id === x.id) ? 'contained' : 'outlined'}
                    onClick={() => handleSelectedCompare(x)}
                  >
                    <Stack sx={{ alignItems: 'flex-start' }}>
                      <Typography variant="body2">{getLogName(x, '', 'Batch')}</Typography>
                      <Typography variant="body2">
                        {x.profile_name || x.profile_data?.name || t('logview.unknownlog')}
                      </Typography>
                      <Typography variant="body2">{toReadableTimestamp(x.start_timestamp)}</Typography>
                    </Stack>
                  </LoadingButton>
                )
              } else {
                return null
              }
            })}
          {logsTotalPages > 1 && (
            <Pagination
              count={logsTotalPages}
              onChange={(_event, page: number): void => {
                setLogsActivePage(page - 1)
              }}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={goToLogsView} disabled={!(selectedCompares && selectedCompares.length >= 2)}>
          {t('logview.compare')}
        </Button>
        <Button onClick={() => props.onClose()}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}
export default CompareLogDialog
